// Phase 7 R1 — purchase order list page (/purchasing).
// Phase 7 R5 — adds date range, free-text search, and Group-by-Vendor toggle.
// Visible to all active shop members; write actions gated inside detail page.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePurchaseOrders, type PurchaseOrderStatus, type PurchaseOrder } from '@/hooks/purchasing/usePurchaseOrders';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { PurchaseOrderStatusBadge } from './PurchaseOrderStatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { vendorKey, pickCanonicalVendorLabel } from '@/lib/purchasing/vendorNormalization';

const STATUS_OPTIONS: Array<{ value: PurchaseOrderStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'partially_received', label: 'Partially Received' },
  { value: 'received', label: 'Received' },
  { value: 'partially_returned', label: 'Partially Returned' },
  { value: 'closed', label: 'Closed' },
  { value: 'canceled', label: 'Canceled' },
];

const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString() : '—';

type ViewMode = 'flat' | 'by-vendor';

export default function PurchaseOrderListPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<PurchaseOrderStatus | 'all'>('all');
  const [vendor, setVendor] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('flat');

  const { data: orders, isLoading } = usePurchaseOrders({ status, vendor, dateFrom, dateTo, search });

  // Phase 7 R5 — client-side grouping by normalized vendor key.
  // Stored vendor_name values are NEVER mutated. Grouping is presentation-only.
  const grouped = useMemo(() => {
    if (!orders) return [];
    const buckets = new Map<string, PurchaseOrder[]>();
    for (const po of orders) {
      const k = vendorKey(po.vendor_name);
      const arr = buckets.get(k) ?? [];
      arr.push(po);
      buckets.set(k, arr);
    }
    return Array.from(buckets.entries())
      .map(([key, pos]) => ({
        key,
        label: pickCanonicalVendorLabel(pos.map((p) => p.vendor_name)),
        orders: pos,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [orders]);

  const renderRow = (po: PurchaseOrder) => (
    <TableRow
      key={po.id}
      className="cursor-pointer"
      onClick={() => navigate(`/purchasing/${po.id}`)}
    >
      <TableCell className="font-mono text-sm">{po.po_number}</TableCell>
      <TableCell>{po.vendor_name || <span className="text-muted-foreground">—</span>}</TableCell>
      <TableCell><PurchaseOrderStatusBadge status={po.status} /></TableCell>
      <TableCell>{fmtDate(po.created_at)}</TableCell>
      <TableCell>{fmtDate(po.expected_at)}</TableCell>
    </TableRow>
  );

  const tableHeader = (
    <TableHeader>
      <TableRow>
        <TableHead>PO #</TableHead>
        <TableHead>Vendor</TableHead>
        <TableHead>Status</TableHead>
        <TableHead>Created</TableHead>
        <TableHead>Expected</TableHead>
      </TableRow>
    </TableHeader>
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Purchasing</h1>
        <p className="text-sm text-muted-foreground">
          Purchase orders for parts. Create POs from a ticket's Parts tab.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3 lg:grid-cols-6">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as PurchaseOrderStatus | 'all')}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs text-muted-foreground">Vendor contains</Label>
            <Input
              placeholder="e.g. NAPA"
              value={vendor}
              onChange={(e) => setVendor(e.target.value)}
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs text-muted-foreground">Search PO # or vendor</Label>
            <Input
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Created from</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Created to</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">
            Purchase Orders {orders && `(${orders.length})`}
          </CardTitle>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList>
              <TabsTrigger value="flat">Flat</TabsTrigger>
              <TabsTrigger value="by-vendor">Group by vendor</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !orders || orders.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/20 p-8 text-center text-sm text-muted-foreground">
              No purchase orders match the current filters.
            </div>
          ) : viewMode === 'flat' ? (
            <Table>
              {tableHeader}
              <TableBody>{orders.map(renderRow)}</TableBody>
            </Table>
          ) : (
            <div className="space-y-6">
              {grouped.map((g) => (
                <div key={g.key}>
                  <div className="mb-2 flex items-baseline justify-between border-b pb-1">
                    <h3 className="text-sm font-semibold">{g.label}</h3>
                    <span className="text-xs text-muted-foreground">
                      {g.orders.length} PO{g.orders.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <Table>
                    {tableHeader}
                    <TableBody>{g.orders.map(renderRow)}</TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
